/**
 * HeroBackground.jsx — Iter295.8
 *
 * Fundo do Hero da Home: paisagem quente âmbar com grafo de dados.
 *
 * Diretiva do usuário (2026-02-20):
 *   "coloque por trás, no mesmo lugar que antes tinhamos a deusa da
 *   justiça, algum grafo em âmbar da nuvem de dados (mesmo em formato
 *   vertical ou irregular), com algum dinamismo, luz e movimento,
 *   mantendo o caráter inovador do site e clean ao mesmo tempo".
 *
 * Camadas:
 *   1. `hero-bg-blob--warm-*` — 3 blobs radiais em tons quentes
 *      (âmbar, cobre, dourado profundo) formando um ambient warm que
 *      substitui o mesh teal/esmeralda anterior. Bem sutis (opacity
 *      0.30-0.40) para não competir com o grafo.
 *   2. `hero-amber-cloud` — SVG posicionado à direita (mesmo slot
 *      onde vivia a Themis). 22 nós irregulares + ~30 arestas em
 *      tonalidade âmbar. Nós pulsam com stagger; arestas têm
 *      `stroke-dashoffset` animado simulando pacotes de dados.
 *   3. `hero-bg-overlay` — degradê slate mantém contraste do texto.
 *
 * O SVG é `preserveAspectRatio="xMaxYMid slice"` — o grafo é
 * ancorado no CANTO DIREITO em qualquer aspect ratio (mesmo em
 * mobile o cluster fica alinhado à direita, não deforma).
 *
 * Respeita `prefers-reduced-motion` via CSS.
 */

import { useEffect, useRef } from "react";

// Grafo âmbar — nós em cluster vertical-irregular na coluna da direita
// do viewBox 1440x920 (roughly x=1000-1400, y=80-860). O padrão NÃO é
// geométrico regular — as posições foram escolhidas para dar sensação
// de nuvem orgânica de dados.
const AMBER_NODES = [
  // Topo (irregular)
  { x: 1080, y: 100, s: 2.2 },   // 0
  { x: 1230, y: 60,  s: 3.0 },   // 1
  { x: 1340, y: 130, s: 2.2 },   // 2
  { x: 1160, y: 180, s: 2.4 },   // 3
  { x: 1290, y: 210, s: 3.2 },   // 4  âncora
  { x: 1400, y: 200, s: 2.4 },   // 5
  // Alto-médio
  { x: 1050, y: 290, s: 2.2 },   // 6
  { x: 1200, y: 320, s: 3.8 },   // 7  âncora forte
  { x: 1360, y: 300, s: 2.6 },   // 8
  // Médio
  { x: 1110, y: 400, s: 2.6 },   // 9
  { x: 1250, y: 450, s: 3.6 },   // 10 âncora forte (centro do grafo)
  { x: 1390, y: 430, s: 2.4 },   // 11
  { x: 1140, y: 530, s: 2.4 },   // 12
  { x: 1300, y: 570, s: 2.8 },   // 13
  // Baixo-médio
  { x: 1050, y: 620, s: 2.2 },   // 14
  { x: 1200, y: 660, s: 3.4 },   // 15 âncora
  { x: 1360, y: 640, s: 2.6 },   // 16
  { x: 1090, y: 730, s: 2.4 },   // 17
  { x: 1240, y: 770, s: 2.8 },   // 18
  { x: 1370, y: 750, s: 2.2 },   // 19
  // Base
  { x: 1170, y: 830, s: 2.4 },   // 20
  { x: 1310, y: 850, s: 2.2 },   // 21
];

const AMBER_EDGES = [
  // Cluster topo
  [0, 1], [1, 2], [1, 3], [1, 4], [2, 5], [3, 4], [4, 5],
  // Topo → alto-médio
  [0, 6], [3, 7], [4, 7], [5, 8], [4, 8],
  // Alto-médio (triângulo estruturante)
  [6, 7], [7, 8],
  // Alto-médio → médio
  [6, 9], [7, 10], [8, 10], [8, 11],
  // Médio
  [9, 10], [10, 11], [9, 12], [10, 13], [12, 13],
  // Médio → baixo-médio
  [12, 14], [13, 15], [11, 16], [13, 16],
  // Baixo-médio
  [14, 15], [15, 16], [14, 17], [15, 18], [16, 19],
  // Baixo-médio → base
  [17, 18], [18, 19], [17, 20], [18, 20], [18, 21], [19, 21],
];

export default function HeroBackground() {
  // Iter295.10 — Parallax de mouse com profundidade em camadas.
  // Registra a posição normalizada do mouse (-1 a +1) em variáveis CSS
  // no wrapper. Cada camada do grafo (halo, linhas, nós) consome essas
  // variáveis com fatores de deslocamento distintos para criar
  // sensação de profundidade real. `requestAnimationFrame` throttle
  // limita atualizações a ~60 fps mesmo com moviments rápidos.
  const parallaxRef = useRef(null);
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    // Respeita `prefers-reduced-motion`: se ativo, o grafo permanece
    // estático (mantém apenas as animações padrão).
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      return undefined;
    }
    let rafId = null;
    let latestX = 0;
    let latestY = 0;
    const handleMove = (e) => {
      // Normaliza para -1..+1 (0 no centro da viewport).
      latestX = (e.clientX / window.innerWidth - 0.5) * 2;
      latestY = (e.clientY / window.innerHeight - 0.5) * 2;
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const el = parallaxRef.current;
        if (el) {
          el.style.setProperty("--mx", latestX.toFixed(3));
          el.style.setProperty("--my", latestY.toFixed(3));
        }
        rafId = null;
      });
    };
    window.addEventListener("mousemove", handleMove, { passive: true });
    return () => {
      window.removeEventListener("mousemove", handleMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <div
      aria-hidden
      data-testid="hero-background"
      className="hero-bg-live pointer-events-none absolute inset-0 overflow-hidden"
    >
      {/* Camada 1: ambient warm — 3 blobs sutis em tons âmbar/cobre.
          Ficam por trás do grafo, criando profundidade sem competir. */}
      <span className="hero-bg-blob hero-bg-blob--warm-1" />
      <span className="hero-bg-blob hero-bg-blob--warm-2" />
      <span className="hero-bg-blob hero-bg-blob--warm-3" />

      {/* Iter295.10 — Wrapper de parallax. Guarda as CSS variables
          `--mx` e `--my` que descendentes consomem em `translate3d`.
          Cada camada tem seu próprio fator de deslocamento (halo <
          linhas < nós), criando profundidade. */}
      <div
        ref={parallaxRef}
        className="hero-amber-parallax"
        data-testid="hero-amber-parallax"
        style={{ "--mx": 0, "--my": 0 }}
      >
        {/* Camada 2: Grafo âmbar (nuvem de dados) na coluna direita.
            preserveAspectRatio ancora o cluster no canto direito
            (`xMaxYMid slice`) — nunca deforma nem invade a coluna do
            texto à esquerda em qualquer viewport. */}
        <svg
          className="hero-amber-cloud"
          viewBox="0 0 1440 920"
          preserveAspectRatio="xMaxYMid slice"
          xmlns="http://www.w3.org/2000/svg"
          data-testid="hero-amber-cloud"
        >
        <defs>
          {/* Iter295.9 — Glow âmbar bem mais intenso (stdDeviation 5)
              + segunda camada de bloom mais larga (stdDeviation 9)
              para dar sensação de luz halo real. */}
          <filter id="amber-glow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="9" result="bloom-far" />
            <feGaussianBlur in="SourceGraphic" stdDeviation="3.5" result="bloom-near" />
            <feMerge>
              <feMergeNode in="bloom-far" />
              <feMergeNode in="bloom-near" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          {/* Iter295.9 — Gradiente de linha bem mais forte, com centro
              muito brilhante (opacity 0.95) — dá sensação de "corrente
              elétrica" ligando os nós. */}
          <linearGradient id="amber-line" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#fbbf24" stopOpacity="0.30" />
            <stop offset="50%"  stopColor="#fde68a" stopOpacity="0.95" />
            <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.30" />
          </linearGradient>
          {/* Glow suave aplicado às próprias linhas — turbina o
              efeito "corrente elétrica" sem custo perceptível. */}
          <filter id="line-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="1.6" result="lg-blur" />
            <feMerge>
              <feMergeNode in="lg-blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Iter295.9 — Halo âmbar radial atrás do cluster; simula um
            "sol" fraco por trás da nuvem de dados. Puramente CSS
            (pintado via SVG rect + radial gradient CSS-only).
            Camada mais funda no parallax (fator 0.4x — mal se mexe). */}
        <rect
          className="hero-amber-halo hero-amber-layer hero-amber-layer--halo"
          x="900" y="80" width="540" height="800"
          fill="url(#amber-halo-radial)"
          aria-hidden="true"
        />
        <defs>
          <radialGradient id="amber-halo-radial" cx="55%" cy="50%" r="55%">
            <stop offset="0%"  stopColor="#fbbf24" stopOpacity="0.20" />
            <stop offset="55%" stopColor="#b45309" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#000000" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Arestas — linhas mais grossas (1.5) e com filtro line-glow.
            Camada "média" no parallax (fator 1x). */}
        <g className="hero-amber-layer hero-amber-layer--lines" stroke="url(#amber-line)" strokeWidth="1.5" strokeLinecap="round" fill="none" filter="url(#line-glow)">
          {AMBER_EDGES.map(([a, b], i) => {
            const na = AMBER_NODES[a];
            const nb = AMBER_NODES[b];
            return (
              <line
                key={`e-${i}`}
                className="hero-amber-line"
                style={{ "--i": i }}
                x1={na.x} y1={na.y}
                x2={nb.x} y2={nb.y}
              />
            );
          })}
        </g>

        {/* Iter295.9 — Anéis de sonar pulsando a partir dos nós
            âncora (r ≥ 3.3). Dão sensação de "wow" tipo transmissão
            de dados. Só 4-5 anéis (baixo custo, alto impacto visual).
            Camada "média-frente" no parallax (fator 1.2x). */}
        <g className="hero-amber-layer hero-amber-layer--rings" fill="none" stroke="#fde68a" strokeWidth="1.1" opacity="0.55" filter="url(#line-glow)">
          {AMBER_NODES.filter((n) => n.s >= 3.3).map((n, i) => (
            <circle
              key={`ring-${i}`}
              className="hero-amber-ring"
              style={{ "--i": i }}
              cx={n.x} cy={n.y} r="4"
            />
          ))}
        </g>

        {/* Nós com glow bloom duplo — o tom depende do raio.
            Camada de frente no parallax (fator 1.6x — mais deslocamento
            para dar sensação de estar mais perto do olho). */}
        <g className="hero-amber-layer hero-amber-layer--nodes" filter="url(#amber-glow)">
          {AMBER_NODES.map((n, i) => (
            <circle
              key={`n-${i}`}
              className="hero-amber-node"
              style={{ "--i": i }}
              cx={n.x}
              cy={n.y}
              r={n.s}
              fill={n.s >= 3.3 ? "#ffffff" : n.s >= 2.6 ? "#fde68a" : "#fbbf24"}
            />
          ))}
        </g>
      </svg>
      </div>

      {/* Camada 3: overlay institucional — mantém contraste do texto. */}
      <div className="hero-bg-overlay" />
    </div>
  );
}
